import type { Permission, Realm, Role } from '@concord/domain';
import { type QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { ApiError, api } from './api';

export interface Me {
  accountId: string;
  role: Role;
  roleLabel: string;
  realm: Realm;
  firstName: string;
  lastName: string;
  email: string;
  timezone: string;
  permissions: Permission[];
  navigator: {
    name: string | null;
    firstName: string | null;
    email: string | null;
    phone: string | null;
    hours: string | null;
  } | null;
  /** Staff only: the authenticator app, and whether to offer setting it up. */
  authenticator: {
    enabled: boolean;
    enabledAt: string | null;
    required: boolean;
    prompt: boolean;
    /** This session passed the app's code (the second step of a staff sign-in). */
    verified: boolean;
  } | null;
  /** Staff only: the navigator user guide, when this environment has one. */
  docsUrl?: string | null;
}

export const ME_KEY = ['me'] as const;

/** The signed-in account, or null when signed out. */
export function useMe() {
  return useQuery({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        return await api<Me>('/me');
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
}

export function useSignOut() {
  const qc = useQueryClient();
  return async (to: string) => {
    await api('/auth/sign-out', { body: {} }).catch(() => undefined);
    qc.clear();
    window.location.assign(to);
  };
}

/** Renders children only for the given realm; otherwise sends the visitor to the right sign-in. */
export function RequireRealm({ realm, children }: { realm: Realm; children: (me: Me) => ReactNode }) {
  const { data: me, isPending } = useMe();
  const location = useLocation();
  if (isPending) return null;
  if (!me || me.realm !== realm) {
    const signIn = realm === 'staff' ? '/console/sign-in' : '/sign-in';
    return (
      <Navigate to={`${signIn}?next=${encodeURIComponent(location.pathname + location.search)}`} replace />
    );
  }
  // Staff with an app enter its code after the emailed one, before anything else.
  if (realm === 'staff' && me.authenticator?.enabled && !me.authenticator.verified) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/console/verify?next=${next}`} replace />;
  }
  // Staff must set up an authenticator app before anything else (staging and production).
  if (
    realm === 'staff' &&
    me.authenticator?.required &&
    !me.authenticator.enabled &&
    !location.pathname.startsWith('/console/security')
  ) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/console/security?setup=1&next=${next}`} replace />;
  }
  return <>{children(me)}</>;
}

/**
 * Where a person goes after signing in with an emailed code or link: staff who have not set
 * up an authenticator app see the setup step first (required on staging and production;
 * elsewhere optional, unless they said "Not now" lately), then carry on to where they were going.
 */
export async function afterSignIn(qc: QueryClient, realm: Realm, next: string): Promise<string> {
  await qc.invalidateQueries({ queryKey: ME_KEY });
  if (realm !== 'staff') return next;
  const me = await qc.fetchQuery({ queryKey: ME_KEY, queryFn: () => api<Me>('/me') });
  if (me.authenticator?.enabled && !me.authenticator.verified)
    return `/console/verify?next=${encodeURIComponent(next)}`;
  return me.authenticator?.prompt ? `/console/security?setup=1&next=${encodeURIComponent(next)}` : next;
}

/** Only same-site paths are honored as a post-sign-in destination. */
export function safeNext(next: string | null, fallback: string): string {
  if (!next?.startsWith('/') || next.startsWith('//')) return fallback;
  return next;
}
